/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import utils from '../../../../common/utils';
import {
  AUDIO_PROVIDERS_QUERY_PARAMS,
  GET_TENANT_AUDIO_PROVIDERS_ERROR,
  GET_TENANT_AUDIO_PROVIDERS_SUCCESS,
  GET_TENANT_PROVIDER_LIST,
  GET_TENANT_PROVIDER_SUCCESS
} from '../../constants/ServiceConstants';
import { normalizeTenantProviderListResponse } from '../../hooks/fetchAudioOptionsConfig';

const resolveTenantProviderEventParams = (event) => {
  const raw = event?.payload ?? event ?? {};
  if (typeof raw === 'string') {
    return { tenantId: raw };
  }
  return raw;
};

export default class TenantProviderServiceV2 extends BaseService {
  constructor() {
    super(GET_TENANT_PROVIDER_LIST, 'tenantProviderListStatus');
  }

  async handleEvent(eventName, event) {
    const { tenantId, audio } = resolveTenantProviderEventParams(event);
    const { isStackB } = utils.getStackBParams();
    const requestConfig = audio ? { params: AUDIO_PROVIDERS_QUERY_PARAMS } : {};

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders({});
      return this.getClient('aiadminstackb').get(`/tenants/${tenantId}/providers`, {
        headers: enhancedHeaders,
        ...requestConfig
      });
    }
    return this.getClient('aiadmin').get(`/tenants/${tenantId}/providers`, requestConfig);
  }

  handleResponse(response, event) {
    const params = resolveTenantProviderEventParams(event);
    const providerList = normalizeTenantProviderListResponse(response?.data);

    if (params.audio) {
      Framework.getEventManager().publish(GET_TENANT_AUDIO_PROVIDERS_SUCCESS, {
        requestId: params.requestId,
        data: providerList
      });
      return;
    }

    Framework.getStoreRegistry().getStore('tenantState').setTenantProviderList(providerList);
    Framework.getEventManager().publish(GET_TENANT_PROVIDER_SUCCESS, {});
  }

  handleError(error, event) {
    const params = resolveTenantProviderEventParams(event);
    const { response } = error;

    if (params.audio) {
      Framework.getEventManager().publish(GET_TENANT_AUDIO_PROVIDERS_ERROR, {
        requestId: params.requestId,
        message: response?.data?.message || error?.message || 'Error fetching audio providers'
      });
      return;
    }

    if (response) {
      const { status } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
    }
    const notification = {
      open: true,
      message: 'Error fetching provider list',
      type: 'error'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
  }
}
